/**
 * Static configuration for the full-screen PDF editor.
 *
 * Everything here is view-agnostic (no React) so the export engine can reuse
 * the same defaults the on-screen painter uses — that is what keeps the
 * downloaded PDF pixel-identical to the preview.
 */

/** Tool ids used by the ribbon, the store and the pointer handlers. */
export const TOOLS = {
    SELECT: 'select',
    HAND: 'hand',
    TEXT: 'text',
    EDIT_TEXT: 'editText',
    REPLACE_IMAGE: 'replaceImage',
    HIGHLIGHT: 'highlight',
    PENCIL: 'pencil',
    IMAGE: 'image',
    SHAPE: 'shape',
    CROSS: 'cross',
    CHECK: 'check',
    SIGN: 'sign',
    NOTE: 'note',
    LINK: 'link',
    UNDERLINE: 'underline',
    STRIKEOUT: 'strikeout',
};

/** Object kinds stored per page. */
export const OBJ = {
    TEXT: 'text',
    INK: 'ink',
    RECT: 'rect',
    ELLIPSE: 'ellipse',
    LINE: 'line',
    ARROW: 'arrow',
    IMAGE: 'image',
    WHITEOUT: 'whiteout',
    CHECK: 'check',
    CROSS: 'cross',
    NOTE: 'note',
    LINK: 'link',
    // A real table: one object carrying its grid + cell text. Movable, resizable
    // and editable as a unit, and (in document mode) it flows with the text.
    TABLE: 'table',
    // A picture of the source page, swapped for another one. It draws nothing
    // itself — the page's own XObject carries the new image — so it exists only
    // to be undone, deleted and exported.
    IMAGE_SWAP: 'imageSwap',
};

/** Tools that create an object by dragging a rectangle. */
export const DRAG_RECT_TOOLS = new Set([
    TOOLS.SHAPE,
    TOOLS.LINK,
    TOOLS.CROSS,
    TOOLS.CHECK,
]);

/** Tools that stream pointer positions into a freehand stroke. */
export const FREEHAND_TOOLS = new Set([TOOLS.PENCIL, TOOLS.HIGHLIGHT]);

/** Tools that act on the pdf.js text layer instead of on free space. */
export const TEXT_LAYER_TOOLS = new Set([
    TOOLS.EDIT_TEXT,
    TOOLS.UNDERLINE,
    TOOLS.STRIKEOUT,
]);

/** Swatches shown in the options bar (brand-neutral, print safe). */
export const COLOR_SWATCHES = [
    '#1C1815', '#3D3833', '#6B635C', '#FFFFFF',
    '#E0492B', '#E68A00', '#F2C200', '#1E9E5E',
    '#1B7BD3', '#5B3FD6', '#C42D8B', '#0F766E',
];

/** The editor's default ink — what "Default" in the colour menu goes back to. */
export const DEFAULT_TEXT_COLOR = '#1C1815';

/** The ribbon's one-click colours, named for their tooltips: ink, red, blue, amber. */
export const QUICK_COLORS = [
    { color: DEFAULT_TEXT_COLOR, name: 'black' },
    { color: '#E0492B', name: 'red' },
    { color: '#1B7BD3', name: 'blue' },
    { color: '#F2C200', name: 'yellow' },
];

/** Line spacings the paragraph stepper walks, single through double. */
export const LINE_HEIGHTS = [1, 1.15, 1.35, 1.5, 1.75, 2];

export const HIGHLIGHT_SWATCHES = [
    '#FFE45C', '#9BFF8C', '#7FD8FF', '#FFA6E0', '#FFB86B', '#C9B6FF',
];

export const DEFAULT_FONT_FAMILY = 'Hind Siliguri';

/**
 * Families pinned to the top of the font picker.
 *
 * The picker itself offers the whole catalogue shared with the print editor
 * (~1,900 Google families plus the local system faces, see `engine/fonts.js`);
 * these are the ones this editor reaches for constantly — Bangla first, because
 * the canvas painter shapes the glyphs itself and that is what keeps conjuncts
 * correct in the export (pdf-lib's own text path cannot shape Bangla).
 */
export const QUICK_FONT_FAMILIES = [
    'Hind Siliguri',
    'Noto Sans Bengali',
    'Noto Serif Bengali',
    'Anek Bangla',
    'Tiro Bangla',
    'Inter',
    'Arial',
    'Helvetica',
    'Times New Roman',
    'Georgia',
    'Courier New',
];

export const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 40, 48, 64, 72];

/**
 * CSS families for one font name.
 *
 * A family name is stored on the object as-is, so this is the one place that
 * decides what stands behind it: Noto Sans Bengali, so a Latin family never
 * drops Bangla text onto whatever the OS happens to pick, and then a generic.
 */
export const fontCss = (family) => `"${family || DEFAULT_FONT_FAMILY}", "Noto Sans Bengali", sans-serif`;

/**
 * CSS families for a text object.
 *
 * "Edit text" copies the *document's own* font: pdf.js loads every embedded
 * font program as a real `@font-face`, so a replaced paragraph can be set in
 * the same typeface rather than the nearest catalogue match. That face is
 * carried on the object as `fontStack`, which already ends with the catalogue
 * CSS so the box still renders once the document — and its faces — are gone.
 */
export const textFontCss = (obj) => obj?.fontStack || fontCss(obj?.fontFamily);

/** Default per-tool settings; the options bar mutates these in the store. */
export const DEFAULT_TOOL_SETTINGS = {
    text: {
        fontFamily: DEFAULT_FONT_FAMILY,
        fontSize: 14,
        color: DEFAULT_TEXT_COLOR,
        bold: false,
        italic: false,
        underline: false,
        align: 'left',
        lineHeight: 1.35,
        background: 'transparent',
    },
    pencil: { color: '#E0492B', strokeWidth: 2.5 },
    highlight: { color: '#FFE45C', strokeWidth: 14 },
    shape: { kind: OBJ.RECT, stroke: '#E0492B', strokeWidth: 2, fill: 'transparent' },
    check: { color: '#1E9E5E', strokeWidth: 3 },
    cross: { color: '#E0492B', strokeWidth: 3 },
    note: { color: '#FFE45C' },
    link: { color: '#1B7BD3' },
    underline: { color: '#E0492B', strokeWidth: 1.5 },
    strikeout: { color: '#E0492B', strokeWidth: 1.5 },
    image: { opacity: 1 },
    sign: { opacity: 1 },
    // Page numbering is a document-wide switch rather than a drawing tool: the
    // only thing to remember is which numerals it stamps.
    pageNumbers: { digits: 'bn' },
};

/**
 * Marks the text objects the page-number switch created, so it can take them
 * away again. Without it a second click just stamped a second number on every
 * page — and the user had to hunt down twenty-five of them by hand.
 */
export const PAGE_NUMBER_ROLE = 'pageNumber';

/**
 * The other document-wide switches, which work the same way: everything they
 * put on the pages carries its role, so pressing the button again takes exactly
 * that away and nothing else. Without a role the only way back from a watermark
 * on a forty-page file is forty deletions.
 */
export const WATERMARK_ROLE = 'watermark';
export const HEADER_ROLE = 'header';
export const FOOTER_ROLE = 'footer';
export const PAGE_BORDER_ROLE = 'pageBorder';
export const LINE_NUMBER_ROLE = 'lineNumber';

/**
 * Footnotes and endnotes, as the three objects each one is made of: the
 * superscript marker in the text, the note itself, and the rule that separates
 * the notes from the page above them.
 */
export const NOTE_MARKER_ROLE = 'noteMarker';
export const NOTE_BODY_ROLE = 'noteBody';
export const NOTE_RULE_ROLE = 'noteRule';

/** Page margins, in points. Word's own three presets, and its default. */
export const MARGIN_PRESETS = {
    normal: { top: 72, right: 72, bottom: 72, left: 72 },
    narrow: { top: 36, right: 36, bottom: 36, left: 36 },
    wide: { top: 72, right: 144, bottom: 72, left: 144 },
    moderate: { top: 72, right: 54, bottom: 72, left: 54 },
};

/** Paper, in points. A PDF page is measured in points and so is this. */
export const PAGE_SIZES = {
    a4: { width: 595.28, height: 841.89 },
    letter: { width: 612, height: 792 },
    legal: { width: 612, height: 1008 },
    a5: { width: 419.53, height: 595.28 },
    a3: { width: 841.89, height: 1190.55 },
};

/**
 * Furniture that is ONE thing appearing on every page, not many things.
 *
 * A watermark is a decision about the document. Dragging the one on page 3 into
 * the corner and leaving the other twenty-four where they were is not something
 * anybody means to do — but that is what happened, because each page carries
 * its own object and an edit reached only the object under the pointer. Moving,
 * resizing, turning or restyling any of these now carries to its twin on every
 * other page.
 *
 * `keep` names the fields that stay each page's own. A page number is the
 * obvious case: the whole point of it is that page 7 says "7".
 *
 * Line numbers are deliberately absent. There are dozens per page at positions
 * that come from that page's own text, so there is no twin to carry a change
 * to, and matching them up by index would move a number onto the wrong line.
 */
export const DOCUMENT_WIDE_ROLES = {
    [WATERMARK_ROLE]: { keep: [] },
    [HEADER_ROLE]: { keep: [] },
    [FOOTER_ROLE]: { keep: [] },
    [PAGE_NUMBER_ROLE]: { keep: ['text', 'marks'] },
    [PAGE_BORDER_ROLE]: { keep: [] },
};

export const isDocumentWideRole = (role) => Object.hasOwn(DOCUMENT_WIDE_ROLES, role || '');

export const ZOOM_STEPS = [0.25, 0.4, 0.5, 0.65, 0.8, 1, 1.25, 1.5, 2, 2.5, 3, 4];
export const ZOOM_MIN = ZOOM_STEPS[0];
export const ZOOM_MAX = ZOOM_STEPS[ZOOM_STEPS.length - 1];

/**
 * Canvas raster scale for the annotations that still have to be stamped
 * (ink, shapes, images, and any text the standard fonts cannot encode).
 * 3× ≈ 216 dpi: soft edges stop being visible against the page's vector text.
 */
export const EXPORT_SCALE = 3;

/** Pages beyond this are still editable but their thumbnails render lazily. */
export const THUMB_WIDTH = 132;

/** Undo history depth. */
export const HISTORY_LIMIT = 60;

export const PAGE_PRESETS = [
    { id: 'a4', label: 'A4', width: 595.28, height: 841.89 },
    { id: 'letter', label: 'Letter', width: 612, height: 792 },
    { id: 'legal', label: 'Legal', width: 612, height: 1008 },
    { id: 'a3', label: 'A3', width: 841.89, height: 1190.55 },
    { id: 'a5', label: 'A5', width: 419.53, height: 595.28 },
];
